import React, { useCallback, useEffect, useState } from "react";
import { AUTH_BASE_URL, ensureArray } from "./config";
import { getAuthToken } from "./auth/authUtils";

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  "Content-Type": "application/json",
});

const responseMessage = async (response, fallback) => {
  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    return parsed.message || parsed.detail || fallback;
  } catch {
    return text || fallback;
  }
};

export default function SubscriptionRequestStatus() {
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState("PREMIUM");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${AUTH_BASE_URL}/auth/subscriptions/me`, { headers: authHeaders() });
      if (!response.ok) throw new Error(await responseMessage(response, "Unable to load subscription status."));
      setData(await response.json());
    } catch (loadError) {
      setError(loadError.message || "Unable to load subscription status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = ensureArray(data?.requests).find(request => request.status === "PENDING");
  const submitRequest = async () => {
    setBusy(true); setMessage(""); setError("");
    try {
      const response = await fetch(`${AUTH_BASE_URL}/auth/subscriptions/requests`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ planCode: plan, note: "Requested from Account Settings" }),
      });
      if (!response.ok) throw new Error(await responseMessage(response, "Subscription request failed."));
      setMessage(`${plan} access request submitted for admin review.`);
      await load();
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  };

  const cancelRequest = async () => {
    if (!window.confirm(`Cancel the pending ${pending.planCode} request?`)) return;
    setBusy(true); setMessage(""); setError("");
    try {
      const response = await fetch(`${AUTH_BASE_URL}/auth/subscriptions/requests/${pending.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!response.ok) throw new Error(await responseMessage(response, "Unable to cancel the request."));
      setMessage("Subscription request cancelled.");
      await load();
    } catch (cancelError) { setError(cancelError.message); }
    finally { setBusy(false); }
  };

  return <section className="subscription-request-status" aria-live="polite">
    <div className="subscription-request-status__heading">
      <div><h4>Subscription access</h4><p>Request an upgrade for administrator approval.</p></div>
      {data && <span className={`subscription-plan-badge subscription-plan-badge--${String(data.planCode).toLowerCase()}`}>{data.planCode}</span>}
    </div>
    {loading && <p className="subscription-request-status__muted">Loading subscription status…</p>}
    {error && <p className="subscription-request-status__error">{error} <button type="button" onClick={load}>Retry</button></p>}
    {!loading && pending && <div className="subscription-request-status__pending">
      <span>Pending request: <strong>{pending.planCode}</strong></span>
      <button type="button" className="account-settings-button account-settings-button--danger" disabled={busy} onClick={cancelRequest}>Cancel request</button>
    </div>}
    {!loading && !pending && <div className="subscription-request-status__actions">
      <select aria-label="Requested subscription plan" value={plan} onChange={event => setPlan(event.target.value)}><option>BASIC</option><option>PREMIUM</option></select>
      <button type="button" className="account-settings-button account-settings-button--primary" disabled={busy} onClick={submitRequest}>{busy ? "Submitting…" : "Request access"}</button>
    </div>}
    {message && <p className="subscription-request-status__success">{message}</p>}
  </section>;
}
